import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { api } from '../../services/api';
import { Permission } from '../../types/permissions';
import { Layout } from '../../components/Layout';
import { PageTitle } from '../../components/PageTitle';
import { Loading } from '../../components/Loading';

export function Permissions() {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadPermissions();
  }, []);

  async function loadPermissions() {
    try {
      setLoading(true);
      const response = await api.get('/permissions');
      setPermissions(response.data);
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message || 'Erro ao carregar permissões',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }

  // Tradução dos recursos para português
  const resourceTranslations: Record<string, string> = {
    'users': 'Usuários',
    'costumers': 'Clientes',
    'costumer-observations': 'Observações de Clientes',
    'services': 'Serviços',
    'equipments': 'Equipamentos',
    'sectors': 'Setores',
    'tech-visit-reports': 'Relatórios de Visita Técnica',
    'zones': 'Zonas',
    'responsibles': 'Responsáveis',
    'dashboard': 'Dashboard',
    'costumers-monitoring': 'Monitoramento de Clientes',
    'costumers-map': 'Mapa de Clientes',
    'profiles': 'Perfis de Acesso',
    'permissions': 'Permissões',
  };

  function translateResource(resource: string): string {
    return resourceTranslations[resource] || resource.replace(/-/g, ' ');
  }

  // Agrupar permissões por recurso
  const groupedPermissions = permissions.reduce((acc, permission) => {
    if (!acc[permission.resource]) {
      acc[permission.resource] = [];
    }
    acc[permission.resource].push(permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  if (loading) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <PageTitle title="Permissões do Sistema" />

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            Esta é a lista de todas as permissões disponíveis no sistema. As permissões são
            atribuídas através de perfis de acesso.
          </p>
        </div>

        <div className="space-y-6">
          {Object.entries(groupedPermissions).map(([resource, perms]) => (
            <div key={resource} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4">
                <h3 className="text-lg font-semibold text-white">
                  {translateResource(resource)}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nome
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ação
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Descrição
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {perms.map(permission => (
                      <tr key={permission.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <code className="text-sm font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">
                            {permission.name}
                          </code>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0085BD]/10 text-[#0085BD]">
                            {permission.action}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600">
                            {permission.description}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Sobre as Permissões
          </h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p>
              <strong>Padrão de nomenclatura:</strong> As permissões seguem o formato{' '}
              <code className="bg-gray-100 px-2 py-1 rounded">recurso:ação</code>
            </p>
            <p>
              <strong>Ações disponíveis:</strong> create (criar), read (visualizar), update
              (editar), delete (deletar), view (visualizar dashboards)
            </p>
            <p>
              <strong>Gestão:</strong> As permissões são fixas no sistema e só podem ser
              atribuídas através dos Perfis de Acesso.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
